import { Link, useParams } from "react-router-dom";
import { ArrowRight, Cpu } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import HumeStrip from "@/components/HumeStrip";
import WorkloadRiskModule from "@/components/WorkloadRiskModule";
import { SPORTS, ENGINES, SPORT_CONTENT } from "@/lib/biohackerBrand";

/**
 * Sport Intelligence Package™ — dedicated per-sport marketing page.
 *
 * Same visual template as BioHacker Baseball™, sport-specific copy pulled
 * from ``SPORT_CONTENT`` in ``biohackerBrand.js``. If a sport has no
 * dedicated content yet the page renders a graceful "coming soon" body
 * without losing the SEO surface.
 */
export default function SportPackagePlaceholder() {
  const { sportId } = useParams();
  const sport = SPORTS.find((s) => s.id === sportId);
  const content = SPORT_CONTENT[sportId];

  if (!sport) {
    return (
      <div className="min-h-screen bg-[#050810] text-white" data-testid="sport-not-found">
        <SiteNav />
        <div className="max-w-4xl mx-auto px-6 py-40 text-center">
          <h1 className="font-display font-black text-3xl">Sport package not found</h1>
          <Link to="/sports" className="mt-6 inline-block text-fuchsia-300 underline">Return to sports hub</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid={`sport-package-${sport.id}`}>
      <SiteNav />

      {/* Hero */}
      <section className="relative pt-28 pb-16 border-b border-white/8 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video src={sport.hero_video} autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050810]/60 via-[#050810]/40 to-[#050810]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            Sport Intelligence Package
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]"
              data-testid={`sport-hero-${sport.id}`}>
            {sport.name}
          </h1>
          <p className="mt-4 text-white/75 max-w-2xl">
            {content?.heroSubtitle || sport.tag}
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid={`sport-engines-${sport.id}`}>
            {sport.engines.map((eid) => {
              const eng = ENGINES.find((e) => e.id === eid);
              if (!eng) return null;
              return (
                <span key={eid} className="text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border"
                  style={{ borderColor: `${eng.tone}55`, color: eng.tone }}
                  data-testid={`sport-engine-${sport.id}-${eid}`}>
                  {eng.name}
                </span>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-demo" data-testid={`sport-cta-demo-${sport.id}`}>
              <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Request demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={`/pricing?sport=${sport.id}`} data-testid={`sport-cta-pricing-${sport.id}`}>
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                See pricing for {sport.name.replace("BioHacker ", "").replace("™","")}
              </Button>
            </Link>
            <Link to="/shop/hume" data-testid={`sport-cta-hume-${sport.id}`}>
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                Shop the Hume Band → $229
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      {content?.pillars && (
        <section className="py-16 max-w-6xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">What the package measures</div>
          <h2 className="font-display font-black text-3xl text-white tracking-tight">Sport-specific intelligence.</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid={`sport-pillars-${sport.id}`}>
            {content.pillars.map((p) => (
              <div key={p.label} className="ink-card rounded-2xl p-6" data-testid={`sport-pillar-${sport.id}-${p.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                <div className="font-display font-black text-white text-lg">{p.label}</div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">{p.body}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Highlight reel — same sport hero, larger cinema treatment */}
      <section className="py-14 border-t border-white/8 bg-black/40" data-testid={`sport-highlight-${sport.id}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
            {sport.name.replace("BioHacker ", "").replace("™", "")} · Highlight reel
          </div>
          <h2 className="font-display font-black text-3xl text-white tracking-tight">
            Every metric on this page is streaming from athletes that look like this.
          </h2>
          <div className="mt-6 relative rounded-2xl overflow-hidden ring-1 ring-white/10">
            <video
              src={sport.hero_video}
              autoPlay muted loop playsInline
              className="w-full h-[420px] object-cover"
              data-testid={`sport-highlight-video-${sport.id}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-6 right-6 flex flex-wrap items-center justify-between gap-4">
              <div className="text-white/85 text-sm">{content?.heroSubtitle || sport.tag}</div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 font-bold">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                Real capture · MotionIQ™ enabled
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles */}
      {content?.audiences && (
        <section className="py-14 border-t border-white/8 max-w-6xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">Built for every role</div>
          <h2 className="font-display font-black text-3xl text-white tracking-tight">One package. Every seat in the building.</h2>
          <div className="mt-8 space-y-4">
            {content.audiences.map((a) => (
              <div key={a.id} className="ink-card rounded-2xl p-6 grid md:grid-cols-4 gap-6"
                   data-testid={`sport-role-${sport.id}-${a.id}`}>
                <div className="font-display font-black text-white text-lg">{a.label}</div>
                <div className="md:col-span-3 text-sm text-white/75 leading-relaxed">{a.body}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Fallback when copy hasn't shipped yet */}
      {!content && (
        <section className="py-16 max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/60">
            This package runs on the same platform as {" "}BioHacker Baseball™. Full sport-specific copy is coming soon.
          </p>
        </section>
      )}

      {/* Hume partnership strip — reinforces the sale on every sport page */}
      <HumeStrip />

      {/* Workload & Structural Risk Analysis — AD / AT / SCSA / Coach breakdown */}
      <WorkloadRiskModule sportId={sport.id} />

      {/* Cross-sell */}
      <section className="py-14 border-t border-white/8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Cpu className="h-8 w-8 mx-auto text-fuchsia-400 mb-3" />
          <h2 className="font-display font-black text-3xl text-white tracking-tight">The rest of the sports catalog</h2>
          <p className="mt-3 text-white/60">Every Sport Intelligence Package™ shares the same platform, cloud, and profile.</p>
          <div className="mt-6">
            <Link to="/sports">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm">
                See all packages <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
